import { createContext, useContext } from "react";

/** What the admin shell shares with its pages: a refresh signal and where to report data freshness. */
export interface AdminShell {
  refreshKey: number;
  reportUpdated: (iso: string) => void;
}

export const AdminShellContext = createContext<AdminShell>({ refreshKey: 0, reportUpdated: () => {} });

export const useAdminShell = () => useContext(AdminShellContext);

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const minutes = Math.max(0, Math.round((now - t) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

const NY_DATE = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" });
const NY_TIME = new Intl.DateTimeFormat(undefined, { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });

/** YYYY-MM-DD of an instant in New York (the backend groups days the same way). */
export function nyDate(at: Date | string): string {
  return NY_DATE.format(typeof at === "string" ? new Date(at) : at);
}

/** Clock time of an instant in New York, e.g. "10:30 AM". */
export function nyTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : NY_TIME.format(d);
}

/** YYYY-MM-DD shifted by whole days (calendar arithmetic, no time zone involved). */
export function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
